import React, { useState, type JSX } from "react";
import { selectProjectLoading, selectProjectInitialized } from "./ProjectSlice_Store";
import { uploadFileToProject } from "./services/fileUploadService";
import { useAppSelector } from "./hooks/reduxHooks";
import { getCurrentUserId } from "./utils/currentUser";
import StatusBanner, { ButtonSpinner } from "./components/StatusBanner";

/** The dashed drop target wrapping the (visually hidden) file input. */
function FileDropZone({
  file,
  uploading,
  onChange,
}: {
  file: File | null;
  uploading: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}): JSX.Element {
  return (
    <div className="rounded-md shadow-sm">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          stroke="currentColor"
          fill="none"
          viewBox="0 0 48 48"
        >
          <path
            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div className="mt-4">
          <label htmlFor="file-input" className="cursor-pointer">
            <span className="mt-2 block text-sm font-medium text-gray-900">
              {file ? file.name : "Choose a file to upload"}
            </span>
            <input
              id="file-input"
              name="file-input"
              type="file"
              className="sr-only"
              onChange={onChange}
              disabled={uploading}
            />
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Any file type supported
          </p>
        </div>
      </div>
    </div>
  );
}

/** Name/size/type summary of the file staged for upload. */
function SelectedFileCard({ file }: { file: File }): JSX.Element {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-blue-800">Selected File</h3>
          <div className="mt-2 text-sm text-blue-700">
            <p><strong>Name:</strong> {file.name}</p>
            <p><strong>Size:</strong> {(file.size / 1024).toFixed(2)} KB</p>
            <p><strong>Type:</strong> {file.type || "Unknown"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Button face: spinner while busy or initializing, plain label otherwise. */
function UploadButtonLabel({
  uploading,
  projectLoading,
  projectId,
}: {
  uploading: boolean;
  projectLoading: boolean;
  projectId: string | null;
}): JSX.Element {
  if (uploading) {
    return (
      <>
        <ButtonSpinner />
        Uploading...
      </>
    );
  }
  if (projectLoading) {
    return (
      <>
        <ButtonSpinner />
        Initializing project...
      </>
    );
  }
  return <>{projectId ? "Upload File" : "Waiting for project..."}</>;
}

/** Destination path and identity footer. */
function UploadFooter({
  projectId,
  projectLoading,
  username,
}: {
  projectId: string | null;
  projectLoading: boolean;
  username: string;
}): JSX.Element {
  let projectLabel = "Waiting for initialization";
  if (projectLoading) {
    projectLabel = "Loading...";
  } else if (projectId) {
    projectLabel = `${projectId.substring(0, 8)}...`;
  }

  return (
    <div className="text-center">
      <p className="text-xs text-gray-500">
        Files are uploaded to: originals/{projectId || "waiting-for-project"}/{"{filename}"}
      </p>
      <p className="text-xs text-gray-400 mt-1">
        Logged in as: {username} | Project: {projectLabel}
      </p>
    </div>
  );
}

/** Progress messages for the stages the upload service reports. */
const UPLOAD_STATUS_TEXT: Record<string, string> = {
  fetching_sas: "Getting upload URL...",
  uploading: "Uploading file...",
};

/** What's missing before an upload can start, or null if it can. */
function uploadBlocker(file: File | null, projectId: string | null): string | null {
  if (!file) {
    return "Please select a file first";
  }
  // Ensure we have a valid project ID before attempting upload
  if (!projectId) {
    return "No project available. Please wait for project to be initialized and try again.";
  }
  return null;
}

/** File selection plus the upload request, with its progress and error state. */
function useUploadPageState() {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Get current user info from Redux auth state only
  const reduxAuth = useAppSelector(state => state.userAuthAndAdminSlice);

  // Get current project ID and loading state from Redux project state
  const { projectId } = useAppSelector(state => state.projectSlice);
  const projectLoading = useAppSelector(selectProjectLoading);
  const _projectInitialized = useAppSelector(selectProjectInitialized);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError("");
      setUploadStatus("");
    }
  };

  const handleUpload = async (): Promise<void> => {
    if (!file || !projectId) {
      setError(uploadBlocker(file, projectId) ?? "");
      return;
    }

    setUploading(true);
    setError("");
    setUploadStatus("Getting upload URL...");

    try {
      const { filePath } = await uploadFileToProject({
        file,
        projectId: projectId,
        username: getCurrentUserId(reduxAuth),
        onStatusChange: (status: string) => {
          const text = UPLOAD_STATUS_TEXT[status];
          if (text) setUploadStatus(text);
        }
      });

      setUploadStatus(`File uploaded successfully to: ${filePath}`);
      setFile(null);
      // Reset file input
      const fileInput = document.getElementById("file-input") as HTMLInputElement | null;
      if (fileInput) fileInput.value = "";

    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setUploadStatus("");
    } finally {
      setUploading(false);
    }
  };

  return {
    file,
    uploading,
    uploadStatus,
    error,
    projectId,
    projectLoading,
    username: getCurrentUserId(reduxAuth),
    handleFileChange,
    handleUpload,
  };
}

export default function UploadPage_Component(): JSX.Element {
  const {
    file,
    uploading,
    uploadStatus,
    error,
    projectId,
    projectLoading,
    username,
    handleFileChange,
    handleUpload,
  } = useUploadPageState();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            File Upload Test
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Test the file-access-service upload functionality
          </p>
        </div>

        <div className="mt-8 space-y-6">
          <FileDropZone file={file} uploading={uploading} onChange={handleFileChange} />

          {file && <SelectedFileCard file={file} />}

          <div>
            <button
              type="button"
              onClick={() => void handleUpload()}
              disabled={!file || uploading || !projectId || projectLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UploadButtonLabel
                uploading={uploading}
                projectLoading={projectLoading}
                projectId={projectId}
              />
            </button>
          </div>

          {uploadStatus && <StatusBanner tone="success" message={uploadStatus} bordered />}
          {error && <StatusBanner tone="error" message={error} bordered />}

          <UploadFooter
            projectId={projectId}
            projectLoading={projectLoading}
            username={username}
          />
        </div>
      </div>
    </div>
  );
}
